/** @jsxImportSource @emotion/react */
import { LoadingOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import type { ReactNode } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from '../Text'
import { renderIconWithColor } from './renderIconWithColor'

type BaseButtonProps = {
  text: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary'
    | 'danger'
    | 'outlined'
    | 'ghost'
  style?: any
  uppercase?: boolean
  /**
   * `compact` is the page-header / tab-bar action size (design-language §8,
   * the profile Edit Player row): 32px tall, `paragraphXSmall` + medium label
   * in Title Case, sized to its content, `opacity 0.85` on hover.
   */
  size?: 'default' | 'compact'
  /**
   * Text/icon color for a non-PRIMARY variant sitting on a light surface (e.g.
   * a `ghost` close button on a cream card). Ignored on PRIMARY fills, whose
   * content always comes from `PRIMARY_BUTTON_CONTENT`.
   */
  contentColor?: string
  /**
   * Which side of the label the icon sits on. `end` is for forward-moving
   * CTAs ("Register Now →"); ignored when `iconOnly`.
   */
  iconPosition?: 'start' | 'end'
}

// `iconOnly` renders a square button with the label hidden; `text` becomes its
// aria-label and tooltip, so it requires an icon.
type ButtonProps = BaseButtonProps &
  ({ iconOnly?: false; icon?: ReactNode } | { iconOnly: true; icon: ReactNode })

const Button = ({
  text,
  onClick,
  disabled,
  isLoading,
  variant,
  style,
  uppercase = false,
  size = 'default',
  iconOnly = false,
  icon,
  contentColor: contentColorOverride,
  iconPosition = 'start',
}: ButtonProps) => {
  const isCompact = size === 'compact'

  // primary and quaternary are both colors.PRIMARY fills, so their content
  // follows the tenant-aware pairing.
  const isPrimaryFill = variant === 'primary' || variant === 'quaternary'

  const contentColor = disabled
    ? colors.NEUTRAL_50
    : isPrimaryFill
    ? PRIMARY_BUTTON_CONTENT.text
    : contentColorOverride ??
      (variant === 'tertiary' ? colors.PRIMARY : colors.WHITE)

  const iconColor = disabled
    ? colors.NEUTRAL_50
    : isPrimaryFill
    ? PRIMARY_BUTTON_CONTENT.icon
    : contentColorOverride ??
      (variant === 'tertiary' ? colors.PRIMARY : colors.WHITE)

  const button = (
    <button
      type='button'
      onClick={onClick}
      aria-label={iconOnly ? text : undefined}
      css={css`
        height: 40px;
        display: flex;
        padding: 8px 24px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 0 0;
        border-radius: 8px;
        cursor: pointer;
        /* A two-word label ("Send Invoice") wrapped to two lines inside the
           40px button and overflowed it — observed live. A button says what it
           does on ONE line; it grows instead. */
        white-space: nowrap;
        ${isLoading ||
        (disabled &&
          css`
            cursor: not-allowed;
            opacity: 0.7;
          `)}
        ${variant === 'primary' &&
        css`
          background-color: ${colors.PRIMARY};
          color: ${PRIMARY_BUTTON_CONTENT.text};
          text-transform: uppercase;
        `}
        ${variant === 'secondary' &&
        css`
          background-color: transparent;
          border: 1px solid ${colors.WHITE};
          color: ${colors.WHITE};
          text-transform: uppercase;
        `}
        ${variant === 'tertiary' &&
        css`
          background-color: ${colors.WHITE};
          border: 1px solid ${colors.PRIMARY};
          color: ${colors.PRIMARY};
          text-transform: uppercase;
        `}
        ${variant === 'quaternary' &&
        css`
          background-color: ${colors.PRIMARY};
          border: 1px solid ${colors.PRIMARY};
          color: ${PRIMARY_BUTTON_CONTENT.text};
          text-transform: uppercase;
        `}
        ${variant === 'danger' &&
        css`
          background-color: ${colors.ERROR_200};
          color: ${colors.WHITE};
          text-transform: uppercase;
        `}
        ${variant === 'outlined' &&
        css`
          background-color: transparent;
          border: 1px solid ${colors.PRIMARY};
          color: ${colors.WHITE};
          text-transform: uppercase;
        `}
        ${variant === 'ghost' &&
        css`
          background-color: transparent;
          border: none;
          color: ${contentColor};
          text-transform: uppercase;
        `}
        ${disabled &&
        css`
          background-color: ${colors.SECONDARY_200};
        `}
        ${isCompact && compactStyle}
        ${iconOnly && iconOnlyStyle}
        ${uppercase &&
        css`
          text-transform: uppercase;
        `}
        ${style}
      `}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <LoadingOutlined
          css={css`
            color: ${isPrimaryFill
              ? PRIMARY_BUTTON_CONTENT.spinner
              : contentColorOverride ?? colors.WHITE};
            font-size: 16px;
          `}
        />
      ) : (
        <>
          {icon && (
            <span
              css={css`
                display: inline-flex;
                align-items: center;
                color: ${iconColor};
                order: ${iconPosition === 'end' ? 1 : 0};
              `}
            >
              {renderIconWithColor(icon, iconColor)}
            </span>
          )}
          {iconOnly ? null : (
            <Text
              variant={isCompact ? 'paragraphXSmall' : 'heading6'}
              weight={isCompact ? 'medium' : 'semiBold'}
              color={contentColor}
            >
              {text}
            </Text>
          )}
        </>
      )}
    </button>
  )

  // An icon-only button has no visible name, so it always carries a tooltip.
  return iconOnly ? <Tooltip title={text}>{button}</Tooltip> : button
}

export default Button

const compactStyle = css`
  height: 32px;
  padding: 0 8px;
  gap: 6px;
  flex: 0 0 auto;
  text-transform: none;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  .anticon {
    font-size: 16px;
  }
`

const iconOnlyStyle = css`
  flex: 0 0 auto;
  aspect-ratio: 1 / 1;
  padding: 0;

  .anticon {
    font-size: 16px;
  }
`
